import {
  afterNextRender,
  Component,
  EventEmitter,
  Inject,
  Input,
  OnInit,
  Output,
  PLATFORM_ID,
  ViewEncapsulation
} from "@angular/core";
import { AwsImage, CmsData } from "@dotcom-frontend/data";
import {
  LgButtonComponent,
  LgModalBodyComponent,
  LgModalComponent,
  LgModalFooterComponent,
  LgModalHeaderComponent,
  LgModalService,
  LgTabItemComponent,
  LgTabItemContentComponent,
  LgTabItemHeadingComponent,
  LgTabsComponent,
  LgToggleComponent
} from "@legal-and-general/canopy";
import { Store } from "@ngrx/store";
import { ComponentLoaderService } from "../../../services/component-loader/component-loader.service";
import { GlobalState } from "../../../store/global/global.reducer";
import { DynamicComponent } from "../../utils/dynamic-loader/dynamic.component";
import { LoaderComponent } from "../../utils/dynamic-loader/loader.component";
import { ImageComponent } from "../../globals/Image/Image.component";
import { Observable } from "rxjs";
import { selectPageTitle } from "../../../store/global/global.selectors";
import { AnalyticsService } from "../../../services/analytics/analytics.service";
import { SafeHtmlPipe } from "../../utils/pipes/safe-html..pipe";
import { CookieService } from "../../../services/cookie/cookie.service";
import { NgClass, NgTemplateOutlet } from "@angular/common";

@Component({
  selector: "dotcom-frontend-disclaimer-popup-block",
  templateUrl: "./DisclaimerPopupBlock.component.html",
  styleUrl: "./DisclaimerPopupBlock.component.scss",
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  imports: [
    ImageComponent,
    LoaderComponent,
    LgModalComponent,
    SafeHtmlPipe,
    LgToggleComponent,
    LgModalHeaderComponent,
    LgModalBodyComponent,
    LgModalFooterComponent,
    LgButtonComponent,
    LgTabsComponent,
    LgTabItemComponent,
    LgTabItemHeadingComponent,
    LgTabItemContentComponent,
    NgTemplateOutlet,
    NgClass
  ]
})
export class DisclaimerPopupBlockComponent implements DynamicComponent, OnInit {
  @Input() data: CmsData | undefined;
  @Output() acceptClicked: EventEmitter<void> = new EventEmitter<void>();
  disclaimerTitle = "";
  pageTitle$: Observable<string> = this.store.select(selectPageTitle);
  logo: AwsImage | undefined;
  disclaimerIntro: string | undefined;
  termsAndConditions: string | undefined;
  checkboxText: string | undefined;
  hasCheckbox = false;
  acceptButtonLabel: string | undefined;
  pageTitle: any;
  adobeData: any | undefined;
  showDisclaimer = true;
  modalSelectorId = "dotcom-modal-selector";
  checkboxChecked = false;
  declineButtons: any;
  blockId: any;
  disclaimerAcceptStatus: string | null = null;
  primaryTabTitle: string | undefined;
  secondaryTab: any | undefined;
  secondaryTabTitle: string | undefined;
  disclaimerStatusSet = false;

  constructor(
    @Inject(PLATFORM_ID) private platformId: any,
    private store: Store<{ global: GlobalState }>,
    private componentLoaderService: ComponentLoaderService,
    private analyticsService: AnalyticsService,
    private modalService: LgModalService,
    private cookieService: CookieService
  ) {
    this.pageTitle$.subscribe((title) => {
      this.pageTitle = title;
    });

    afterNextRender(() => {
      if (this.disclaimerAcceptStatus !== "accepted") {
        this.openModal();
      }
    });
  }

  async ngOnInit() {
    if (this.data) {
      if (this.data["disclaimerTitle"]) {
        this.disclaimerTitle = this.data["disclaimerTitle"];
      }

      if (this.data["logo"]) {
        this.logo = this.data["logo"];
      }

      if (this.data["disclaimerIntro"]) {
        this.disclaimerIntro = this.data["disclaimerIntro"];
      }

      if (this.data["termsAndConditions"]) {
        this.termsAndConditions = this.data["termsAndConditions"];
      }

      if (this.data["checkboxText"]) {
        this.checkboxText = this.data["checkboxText"];
        this.hasCheckbox = true;
      }

      if (this.data["acceptButtonLabel"]) {
        this.acceptButtonLabel = this.data["acceptButtonLabel"];
      }

      if (this.data["contentLink"]) {
        this.blockId = this.data["contentLink"]["id"];
        this.modalSelectorId = `${this.modalSelectorId}-${this.blockId}`;
      }

      if (this.data["declineButton"]) {
        this.declineButtons = [];
        for (const button of this.data["declineButton"]) {
          const buttonComponent =
            await this.componentLoaderService.loadComponent(
              "Button",
              "globals",
              {
                href: button.href,
                text: button.text,
                target: "_self",
                title: button.title,
                buttonStyle: "secondary",
                adobeData: {
                  interactionContainer: "Disclaimer Popup Block",
                  interactionLink: button["href"],
                  interactionName: button["text"],
                  interactionPage: this.pageTitle,
                  interactionRegion: "",
                  interactionType: "ButtonClick",
                  interactionDetails: ""
                }
              }
            );
          this.declineButtons.push(buttonComponent);
        }
      }

      if (this.data["bottomSpacing"]) {
        this.data["bottomSpacing"] = "block-spacing--zero";
      }

      if (this.data["secondaryTabContent"]) {
        this.primaryTabTitle = this.data["primaryTabTitle"];
        this.secondaryTabTitle = this.data["secondaryTabTitle"];
        this.secondaryTab = await this.componentLoaderService.contentAreaLoader(
          this.data["secondaryTabContent"]
        );
      }
    }

    const cookieName = `disclaimerAcceptStatus_${this.blockId}`;

    this.disclaimerAcceptStatus = this.cookieService.getCookie(cookieName);

    if (this.disclaimerAcceptStatus === "accepted") {
      this.showDisclaimer = false;
    }
    this.disclaimerStatusSet = true;
  }

  setDisclaimerCookie(blockId: string) {
    const cookieName = `disclaimerAcceptStatus_${blockId}`;
    this.cookieService.setCookie(cookieName, "accepted", 30);
    this.showDisclaimer = false;
  }

  openModal() {
    this.modalService.open(this.modalSelectorId);
  }

  onCheckboxChange(event: any) {
    this.checkboxChecked = event.target.checked;
  }

  onAccept(modalSelector: string) {
    if (!this.checkboxText || this.checkboxChecked) {
      this.acceptClicked.emit();
      this.setDisclaimerCookie(this.blockId);
      this.modalService.close(modalSelector);
      this.showDisclaimer = false;
    }
  }

  interactionClickEvent(clickUrl: any, clickName: any) {
    this.analyticsService.adobeInteractionEvent(
      "Disclaimer Popup Block",
      clickUrl,
      clickName,
      "",
      "LinkClick",
      this.pageTitle,
      ""
    );
  }
}
